import React from 'react';
import { Button, ButtonProps, styled } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import {
  GRADIENT_PRIMARY,
  pulseAnimation,
  gradientShiftAnimation,
  SECONDARY_TEXT_COLOR_HOVER,
} from 'components/AI/AIStyles';

const StyledAIButton = styled(Button)(({ theme, variant }) => ({
  position: 'relative',
  transition: 'all ease-in-out 350ms',
  ...(variant === 'contained' && {
    border: '0',
    background: GRADIENT_PRIMARY,
    color: theme.palette.common.white,
    '&:hover': {
      color: theme.palette.common.white,
      animation: `${pulseAnimation} 2s 1`,
      boxShadow: '0px 5px 20px rgba(0, 0, 0, 0.1)',
    },
  }),
  ...(variant === 'outlined' && {
    position: 'relative',
    borderRadius: '20px',
    background: 'white',
    color: '#00afcc',
    border: '2px solid transparent',
    backgroundImage: `linear-gradient(white, white), ${GRADIENT_PRIMARY}`,
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      background: 'white',
      backgroundImage: `linear-gradient(white, white), linear-gradient(270deg, #00afcc, #ec2aab, #00afcc, #ec2aab)`,
      backgroundSize: 'auto, 400% 400%',
      border: '2px solid transparent',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      animationName: gradientShiftAnimation,
      animationDuration: '3s',
      animationIterationCount: 'infinite',
      animationTimingFunction: 'ease-in-out',
    },
  }),
  ...(variant === 'text' && {
    color: '#00afcc',
    '&:hover': {
      color: SECONDARY_TEXT_COLOR_HOVER,
      animation: `${pulseAnimation} 2s 1`,
    },
  }),
}));

export interface AIButtonProps extends Omit<ButtonProps, 'startIcon'> {
  /**
   * Text to display on the button
   */
  text?: string;
  /**
   * Whether to show the icon
   */
  showIcon?: boolean;
  /**
   * Function to call when the button is clicked
   */
  onAIAction?: () => void;
}

/**
 * AI Button component with a sparkling icon
 *
 * @param props - Component props
 * @returns React component
 */
export const AIButton: React.FC<AIButtonProps> = ({
  text = 'AI',
  showIcon = true,
  onAIAction,
  onClick,
  children,
  variant,
  ...rest
}) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) onClick(event);
    if (onAIAction) onAIAction();
  };

  return (
    <StyledAIButton
      variant={variant}
      startIcon={showIcon ? <AutoAwesome /> : undefined}
      onClick={handleClick}
      {...rest}
    >
      {children || text}
    </StyledAIButton>
  );
};

export default AIButton;
